import React, { useState, useEffect, useCallback, useMemo } from 'react';
import DefaultScreen from '../../DefaultScreen';
import clsx from 'clsx';
import { debounce } from 'lodash';
import {
  Typography,
  Grid,
  Button,
  TextField,
  Box,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Link
} from '@material-ui/core';
import { ViewModule, ViewList, Add, Restore, Language, Code } from '@material-ui/icons';
import { FaChartPie } from "react-icons/fa";
import { SurveryResultsIcon } from '../../../assets/images/managment';
import { exportSurvey } from '../../../redux/reducers/landingPagesSlice';
import { ExportFile } from '../../../helpers/Export/ExportFile';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { getPerformanceStats, getPopupPages, Page, deletePopup, getDeletedPopups } from '../../../../src/redux/reducers/popUpManagementSlice';
import { restoreLandingPages, duplicteLandingPage, getPageHeight } from '../../../../src/redux/reducers/landingPagesSlice';
import { Title } from '../../../components/managment/Title';
import { Loader } from '../../../components/Loader/Loader';
import Toast from '../../../components/Toast/Toast.component';
import { actionURL, isProdMode } from '../../../config';
import { ManagmentIcon, TablePagination, RestorDialogContent, PopMassage } from '../../../components/managment/index';
import PopUpCard from './PopUpCard';
import StatCard from './StatCard';
import PopupPreviewModal from './PopupPreviewModal';
import { CopyIcon, DeleteIcon, DuplicateIcon, EditIcon, PreviewIcon, SettingIcon, ReportsIcon } from '../../../assets/images/managment';
import { BaseDialog } from '../../../components/DialogTemplates/BaseDialog';
import { sitePrefix } from '../../../config';
import { ConvertObjectToQueryString } from '../../../helpers/Utils/HtmlUtils';
import { CLIENT_CONSTANTS } from '../../../model/Clients/Contants';
import { useNavigate, useLocation } from 'react-router-dom';
import { findPlanByFeatureCode } from '../../../../src/redux/reducers/TiersSlice';
import TierPlans from '../../../components/TierPlans/TierPlans';
import { TierFeatures } from '../../../helpers/Constants';
import { get } from 'lodash';

interface PopUpManagementProps {
  classes: Record<string, string>;
}

interface ToastMessage {
  type: 'error' | 'success' | 'info';
  message: string;
}

export interface DialogType {
  type: 'delete' | 'restore' | 'duplicate' | 'embed' | 'tier';
  data: any;
}

const PopUpManagement: React.FC<PopUpManagementProps> = ({ classes }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch<any>();
  const { windowSize } = useSelector((state: any) => state.core);
  const { userRoles } = useSelector((state: any) => state.core);
  const { currentPlan, availablePlans } = useSelector((state: any) => state.tiers);
  const { subAccount, isRTL } = useSelector((state: any) => state.common);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const {
    stats,
    statsLoading,
    statsError,
    pages,
    totalPages,
    currentPage,
    pagesLoading,
    pagesError,
    deletedPopups,
  } = useSelector((state: any) => state.popUpManagement);

  const [view, setView] = useState<'card' | 'table'>('card');
  const [toastMessage, setToastMessage] = useState<ToastMessage | null>(null);
  const [dialogType, setDialogType] = useState<DialogType | null>(null);
  const [restoreArray, setRestoreArray] = useState<number[]>([]);
  const [previewPopupId, setPreviewPopupId] = useState<number | null>(null);
  const [showLoader, setShowLoader] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showTierPlans, setShowTierPlans] = useState(false);
  const [filters, setFilters] = useState({
    SearchTerm: '',
    FilterStatus: 'All',
    SortBy: 'CreatedDate',
    SortDirection: 'DESC',
    PageNumber: 1,
    PageSize: 6,
    PageType: 5
  });


  const handleCreatePopup = () => {
    navigate(`${sitePrefix}Popups/Create`);
  };

  const onExportSurvey = async (id: number) => {
    // @ts-ignore
    const surveysResponse = await dispatch(exportSurvey(id));
    const surveys = surveysResponse?.payload;
    const fields = surveys?.length > 0 && Object.keys(surveys[0]);
    ExportFile({
      data: surveys,
      fileName: 'surveyReport',
      exportType: 'xls',
      fields: fields
    });
  };

  const isMobile = windowSize === 'xs' || windowSize === 'sm';

  // Create debounced search function
  const debouncedSearch = useMemo(
    () => debounce((term: string) => {
      setFilters(prev => ({ ...prev, SearchTerm: term, PageNumber: 1 }));
    }, 500),
    []
  );

  // Immediate search function for first search or clearing
  const immediateSearch = useCallback((term: string) => {
    setFilters(prev => ({ ...prev, SearchTerm: term, PageNumber: 1 }));
  }, []);

  useEffect(() => {
    const navState = location.state as { view?: 'card' | 'table' } | undefined;
    if (navState?.view) {
      setView(navState.view);
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  useEffect(() => {
    if (searchTerm === filters.SearchTerm) {
      return;
    }

    // Execute immediately if search term is empty (clearing search)
    if (searchTerm === '') {
      immediateSearch(searchTerm);
      return;
    }

    // Use debounced search for non-empty terms
    debouncedSearch(searchTerm);

    // Cleanup function to cancel pending debounced calls
    return () => {
      debouncedSearch.cancel();
    };
  }, [searchTerm, filters.SearchTerm, debouncedSearch, immediateSearch]);

  // Cleanup debounced function on unmount
  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  useEffect(() => {
    const fetchInitialData = async () => {
      await Promise.all([
        dispatch(getPerformanceStats()),
        dispatch(getPopupPages(filters)),
        dispatch(getDeletedPopups())
      ]);
      setIsInitialLoad(false);
    };

    fetchInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isInitialLoad) {
      dispatch(getPopupPages(filters));
    }
  }, [dispatch, filters, isInitialLoad]);

  useEffect(() => {
    if (statsError) {
      setToastMessage({ type: 'error', message: statsError });
    }
    if (pagesError) {
      setToastMessage({ type: 'error', message: pagesError });
    }
  }, [statsError, pagesError]);

  const renderTopSection = () => (
    <Box>
      <Box pt={4}>
        <Title
          Text={t('landingPages.popupManagement.title')}
          classes={classes}
        />
      </Box>
      <Box pt={3} className={classes.responsiveActions}>
        <Button
          variant="contained"
          color="primary"
          className={clsx(classes.btn, classes.btnRounded, classes.mr10)}
          startIcon={<Add />}
          onClick={handleCreatePopup}
        >
          {t('landingPages.popupManagement.createNew')}
        </Button>
        <Button
          variant="outlined"
          className={clsx(classes.btn, classes.btnRounded)}
          startIcon={<Restore />}
          disabled={!deletedPopups || deletedPopups.length === 0}
          onClick={() => setDialogType({ type: 'restore', data: deletedPopups })}
        >
          {t('campaigns.restoreDeleted')}
        </Button>
      </Box>
      <Box mt={3}>
        {stats ? (
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                classes={classes}
                title={t('landingPages.popupManagement.statCards.totalPopups')}
                value={stats.TotalPopups.toString()}
                change={`${stats.ActiveCount} ${stats.ActiveCount === 1 ? t('landingPages.popupManagement.filters.active') : t('landingPages.popupManagement.filters.activePlural')} • ${stats.InactiveCount} ${stats.InactiveCount === 1 ? t('landingPages.popupManagement.filters.inactive') : t('landingPages.popupManagement.filters.inactivePlural')} • ${stats.DraftCount} ${t('landingPages.popupManagement.filters.draft')}`}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                classes={classes}
                title={t('landingPages.popupManagement.statCards.viewsThisMonth')}
                value={stats.MonthlyViews.toLocaleString()}
                change={`${stats.MonthlyViewsChange > 0 ? '+' : ''}${stats.MonthlyViewsChange.toFixed(2)}% ${t('landingPages.popupManagement.statCards.thisMonth')}`}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                classes={classes}
                title={t('landingPages.popupManagement.statCards.avgConversionRate')}
                value={`${stats.AverageConversionRate.toFixed(2)}%`}
              // change={`${stats.AverageConversionChange > 0 ? '+' : ''}${stats.AverageConversionChange.toFixed(2)}% this week`}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                classes={classes}
                title={t('landingPages.popupManagement.statCards.topPerforming')}
                value={stats.TopPerformer ? stats.TopPerformer.Name : t('landingPages.popupManagement.statCards.thisMonth')}
                change={stats.TopPerformer ? `${stats.TopPerformer.ConversionRate.toFixed(2)}% ${t('landingPages.popupManagement.statCards.conversion')}` : ''}
              />
            </Grid>
          </Grid>
        ) : null}
      </Box>
    </Box>
  );

  const handleSearch = () => {
    setFilters(prev => ({ ...prev, SearchTerm: searchTerm, PageNumber: 1 }));
  };

  const handleTierRestriction = (message: string) => {
    setDialogType({ type: 'tier', data: message });
  };

  const handleGetPlanForFeature = (tierMessageCode: string) => {
    const planName = findPlanByFeatureCode(
      tierMessageCode,
      availablePlans,
      currentPlan.Id
    );

    if (planName) {
      return t('billing.tier.featureNotAvailable')
        .replace('{feature}', t(TierFeatures[tierMessageCode as keyof typeof TierFeatures] || tierMessageCode))
        .replace('{planName}', planName);
    } else {
      return t('billing.tier.noFeatureAvailable');
    }
  };

  const getTierValidationDialog = (data: string) => ({
    title: t('billing.tier.permission'),
    showDivider: false,
    content: (
      <Typography style={{ fontSize: 18 }} className={clsx(classes.textCenter)}>
        {handleGetPlanForFeature(data || '')}
      </Typography>
    ),
    renderButtons: () => (
      <Grid
        container
        spacing={2}
        className={clsx(
          classes.dialogButtonsContainer,
          isRTL ? classes.rowReverse : null,
          !get(subAccount, 'CompanyAdmin', false) ? classes.dNone : ''
        )}
      >
        <Grid item>
          <Button
            onClick={() => {
              setDialogType(null);
              setShowTierPlans(true);
            }}
            className={clsx(classes.btn, classes.btnRounded)}
          >
            {t('billing.upgradePlan')}
          </Button>
        </Grid>
        <Grid item>
          <Button
            onClick={() => setDialogType(null)}
            className={clsx(classes.btn, classes.btnRounded)}
          >
            {t('common.cancel')}
          </Button>
        </Grid>
      </Grid>
    )
  });


  const renderSearchAndFilterSection = () => {
    const filterButtons = [
      { value: 'All', labelKey: 'all' },
      { value: 'Active', labelKey: 'active' },
      { value: 'Inactive', labelKey: 'inactive' },
      { value: 'Draft', labelKey: 'draft' }
    ];

    return (
      <Box className={classes.topSection} mt={3} p={1}>
        <Grid className={clsx(classes.popupCard, classes.p10)} container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
              }}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              placeholder={t('landingPages.popupManagement.searchPlaceholder')}
            />
          </Grid>
          <Grid item>
            {filterButtons.map(filter => (
              <Button
                key={filter.value}
                variant={filters.FilterStatus === filter.value ? "contained" : "text"}
                color={filters.FilterStatus === filter.value ? "primary" : "default"}
                className={classes.btnRounded}
                onClick={() => setFilters(prev => ({ ...prev, FilterStatus: filter.value, PageNumber: 1 }))}
                style={{ marginRight: '5px' }}
              >
                {t(`landingPages.popupManagement.filters.${filter.labelKey}`)}
              </Button>
            ))}
          </Grid>
          <Grid item xs>
            {/* Spacer */}
          </Grid>
          <Grid item>
            <FormControl variant="outlined" size="small" style={{ minWidth: 120, marginRight: '10px' }}>
              <InputLabel>{t('landingPages.popupManagement.filters.sortBy')}</InputLabel>
              <Select
                value={filters.SortBy}
                onChange={(e) => setFilters(prev => ({ ...prev, SortBy: e.target.value as string }))}
                label={t('landingPages.popupManagement.filters.sortBy')}
              >
                <MenuItem value="CreatedDate">{t('landingPages.popupManagement.filters.createdDate')}</MenuItem>
                <MenuItem value="Name">{t('landingPages.popupManagement.filters.name')}</MenuItem>
                <MenuItem value="Views">{t('landingPages.popupManagement.filters.views')}</MenuItem>
                <MenuItem value="ConversionRate">{t('landingPages.popupManagement.filters.conversionRate')}</MenuItem>
                <MenuItem value="LastModified">{t('landingPages.popupManagement.filters.lastModified')}</MenuItem>
              </Select>
            </FormControl>
            <FormControl variant="outlined" size="small" style={{ minWidth: 100 }}>
              <InputLabel>{t('landingPages.popupManagement.filters.direction')}</InputLabel>
              <Select
                value={filters.SortDirection}
                onChange={(e) => setFilters(prev => ({ ...prev, SortDirection: e.target.value as string }))}
                label={t('landingPages.popupManagement.filters.direction')}
              >
                <MenuItem value="ASC">{t('landingPages.popupManagement.filters.ascending')}</MenuItem>
                <MenuItem value="DESC">{t('landingPages.popupManagement.filters.descending')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item>
            <IconButton onClick={() => setView('card')} color={view === 'card' ? 'primary' : 'default'}>
              <ViewModule />
            </IconButton>
            <IconButton onClick={() => setView('table')} color={view === 'table' ? 'primary' : 'default'}>
              <ViewList />
            </IconButton>
          </Grid>
        </Grid>
      </Box>
    );
  };

  const renderCardView = () => {
    const groupedByDomain = pages.reduce((acc: Record<string, Page[]>, page: Page) => {
      page.Domains.forEach(domain => {
        if (!acc[domain]) {
          acc[domain] = [];
        }
        if (!acc[domain].some(p => p.ID === page.ID)) {
          acc[domain].push(page);
        }
      });
      return acc;
    }, {} as Record<string, Page[]>);

    const handleDomainEmbed = (domain: string, domainPages: Page[]) => {
      const activePopups = domainPages.filter(
        p => p.StatusName === 'Active' && p.PopupGuid && p.PopupGuid.trim() !== ''
      );

      if (activePopups.length === 0) {
        setToastMessage({
          type: 'info',
          message: t('landingPages.popupManagement.noActivePopupsForDomain')
        });
        return;
      }

      setDialogType({
        type: 'embed',
        data: activePopups[0]
      });
    };

    return (
      <Box>
        {(Object.entries(groupedByDomain) as [string, Page[]][]).map(([domain, domainPages]) => {
          const hasActivePopup = domainPages.some(p => p.StatusName === 'Active');
          // const hasActivePopup = domainPages.some(p => p.StatusName === 'Active');
          const hasPopupGuid = domainPages.some(p => p.PopupGuid && p.PopupGuid.trim() !== '');

          return (
            <Box key={domain} mb={4}>
              {/* Domain Header */}
              <Box
                display="flex"
                alignItems="center"
                mb={2}
                p={2}
                className={clsx(classes.popupCard, classes.domainHeader)}
                style={{
                  borderRadius: '8px',
                  backgroundColor: 'var(--pls-d-surface, #f8f9fa)'
                }}
              >
                <Box
                  width={40}
                  height={40}
                  borderRadius="50%"
                  bgcolor="primary.main"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  mr={2}
                >
                  <Language style={{ color: '#fff', fontSize: 22 }} />
                </Box>
                <Typography variant="h6" style={{ fontWeight: 600, marginRight: 16 }}>
                  {domain}
                </Typography>

                <Button
                  variant="outlined"
                  size="small"
                  className={clsx(
                    classes.btn,
                    classes.btnRounded,
                    classes.ml5
                  )}
                  startIcon={<Code />}
                  onClick={() => handleDomainEmbed(domain, domainPages)}
                  // disabled={!hasActivePopup}
                  disabled={!hasActivePopup || !hasPopupGuid}
                >
                  {t('landingPages.popupManagement.actions.embed')}
                </Button>
              </Box>

              {/* Cards for this domain */}
              <Grid container spacing={3}>
                {domainPages.map((page: Page) => (
                  <Grid item xs={12} key={page.ID}>
                    <PopUpCard
                      popup={page}
                      classes={classes}
                      setDialogType={setDialogType}
                      onTierRestriction={handleTierRestriction}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
          );
        })}
      </Box>
    );
  };

  const renderActionIcons = (page: Page) => {
    const id = page.ID;
    const isActive = page.StatusName === 'Active';
    const isSurvey = page.IsSurvey;
    const surveyCount = page.SurveyCount || 0;
    const isNewEditor = page.IsNewEditor;

    const iconsMap = [
      {
        key: isNewEditor ? `${id}_surveyGraph` : `${id}_surveyExport`,
        uIcon: isNewEditor ? FaChartPie : SurveryResultsIcon,
        lable: isNewEditor
          ? t('landingPages.SurveyExportTitle')
          : `${t('landingPages.SurveyExportTitle')} (${surveyCount})`,
        rootClass: clsx(
          classes.paddingIcon,
          !isNewEditor && !userRoles?.AllowExport && classes.disabled
        ),
        disable: !isSurvey || (!isNewEditor && surveyCount === 0) || (!isNewEditor && !userRoles?.AllowExport),
        onClick: () => {
          if (isNewEditor) {
            navigate(`${sitePrefix}Popups/SurveyDetails/${id}`, {
              state: {
                PageProperty: {
                  PageNumber: filters.PageNumber,
                  SearchTerm: filters.SearchTerm
                }
              }
            });
          } else {
            if (userRoles?.AllowExport) {
              onExportSurvey(id);
            }
          }
        }
      },
      {
        key: "Settings",
        uIcon: SettingIcon,
        lable: t('landingPages.popupManagement.actions.settings'),
        rootClass: classes.paddingIcon,
        onClick: () => {
          navigate(`${sitePrefix}Popups/DisplayRules/${id}`, {
            state: {
              returnView: view,
            }
          });
        }
      },
      {
        key: "preview",
        uIcon: PreviewIcon,
        lable: t('landingPages.popupManagement.actions.preview'),
        rootClass: classes.paddingIcon,
        onClick: () => {
          setPreviewPopupId(page.ID);
        }
      },
      {
        key: "edit",
        uIcon: EditIcon,
        lable: t('landingPages.EditResource1.HeaderText'),
        rootClass: classes.paddingIcon,
        onClick: () => {
          navigate(`${sitePrefix}popupeditor/${id}`);
        }
      },
      {
        key: "duplicate",
        uIcon: DuplicateIcon,
        lable: t('campaigns.lnkEditResource1.ToolTip'),
        rootClass: classes.paddingIcon,
        onClick: () => {
          setDialogType({
            type: 'duplicate',
            data: id
          });
        }
      },
      {
        key: "embed",
        uIcon: CopyIcon,
        lable: t('landingPages.popupManagement.actions.embed'),
        rootClass: classes.paddingIcon,
        disable: !isActive,
        type: 'embed',
        onClick: () => {
          if (isActive) {
            setDialogType({
              type: 'embed',
              data: page
            });
          }
        }
      },
      {
        key: "delete",
        uIcon: DeleteIcon,
        lable: t('landingPages.GridButtonColumnResource1.HeaderText'),
        rootClass: classes.paddingIcon,
        onClick: () => {
          setDialogType({
            type: 'delete',
            data: id
          });
        }
      },
    ];

    return (
      <Grid container justifyContent="center" alignItems='center'>
        {iconsMap
          .filter(icon => !icon.disable || isSurvey)
          .map(icon => {
            const { onClick, type, uIcon: IconComponent, ...restProps } = icon;

            let iconElement;
            if (icon.key.includes('survey')) {
              iconElement = React.isValidElement(IconComponent)
                ? IconComponent
                : React.createElement(IconComponent as any, {
                  className: 'rowIcon',
                  style: { fontSize: 20 }
                });
            } else {
              iconElement = <IconComponent width={18} height={20} className={'rowIcon'} />;
            }

            const iconProps: any = {
              ...restProps,
              classes,
              icon: null,
              uIcon: iconElement
            };

            if (onClick) {
              iconProps.onClick = onClick;
            }

            return (
              <Grid
                className={clsx('rowIconContainer', classes.actionIconsContainer)}
                key={icon.key}
                item
              >
                <ManagmentIcon {...iconProps} />
              </Grid>
            );
          })}
      </Grid>
    );
  };

  const renderSubscribersCell = (page: Page) => {
    const { ID, Submits, Name } = page;
    const subscribtions = Submits && Submits > 0;
    const hasRecipientAccess = !userRoles?.HideRecipients;

    const linkUrl = hasRecipientAccess
      ? `${CLIENT_CONSTANTS.BASEURL}${ConvertObjectToQueryString({
        ...CLIENT_CONSTANTS.QUERY_PARAMS,
        CampaignID: ID,
        PageType: CLIENT_CONSTANTS.PAGE_TYPES.FormID,
        ResultTitle: `${t("common.clientSubscriptionResultTitle")} "${Name}"`
      })}`
      : undefined;

    return (
      <>
        <Link
          component='a'
          href={linkUrl}
          style={{
            cursor: subscribtions && hasRecipientAccess ? 'pointer' : 'default',
            textDecoration: subscribtions && hasRecipientAccess ? 'underline' : 'none',
          }}
          onClick={(e) => {
            e.preventDefault();
            if (Submits && Submits > 0 && hasRecipientAccess) {
              navigate(CLIENT_CONSTANTS.BASEURL, {
                state: {
                  ...CLIENT_CONSTANTS.QUERY_PARAMS,
                  CampaignID: ID,
                  PageType: CLIENT_CONSTANTS.PAGE_TYPES.FormID,
                  ResultTitle: `${t("common.clientSubscriptionResultTitle")} "${Name}"`
                }
              });
            } else {
              return false;
            }
          }}
          className={clsx(classes.middleText, classes.flexColumnCenter, classes.pt2, userRoles?.HideRecipients && classes.disabled)}
        >
          <Typography className={classes.middleText}>
            {(Submits && Submits.toLocaleString()) || '0'}
          </Typography>
          <Typography className={classes.middleText}>
            {t('landingPages.SubmitsResource1.HeaderText')}
          </Typography>
        </Link>
      </>
    );
  };

  const renderDesktopTableRow = (page: Page) => {
    const rowStyle = { head: classes.tableRowHead, root: classes.tableRowRoot };
    const cellStyle = { head: classes.tableCellHead, body: classes.tableCellBody, root: classes.tableCellRoot };

    return (
      <TableRow key={page.ID} classes={rowStyle} className={classes.p5}>
        <TableCell classes={cellStyle} className={clsx(classes.flex2)}>
          <Typography variant="h5" className={clsx(classes.f22, classes.breakText)}>
            {page.Name}
          </Typography>
          <Typography variant="body1" className={classes.breakText}>
            {page.Domains.join(', ')}
          </Typography>
        </TableCell>
        <TableCell classes={cellStyle} className={classes.flex1}>
          <Typography variant="body1">
            {page.AllViews?.toLocaleString() ?? 'N/A'}
          </Typography>
          <Typography variant="body1">
            ({page.StatusName})
          </Typography>
        </TableCell>
        <TableCell classes={cellStyle} className={classes.flex1}>
          {page.IdentifiedViewers?.toLocaleString() ?? 'N/A'}
        </TableCell>
        <TableCell classes={cellStyle} className={classes.flex1}>
          {renderSubscribersCell(page)}
        </TableCell>
        <TableCell classes={cellStyle} className={classes.flex1}>
          <Typography variant="body1">
            {page.Conversions?.toLocaleString() ?? 'N/A'}
          </Typography>
          <Typography variant="body1">
            ({page.ConversionRate ? `${page.ConversionRate}%` : 'N/A'})
          </Typography>
        </TableCell>
        <TableCell classes={cellStyle} className={clsx(classes.flex3, classes.tableActionContainerCell)}>{renderActionIcons(page)}</TableCell>
      </TableRow>
    );
  }

  const renderMobileTableRow = (page: Page) => (
    <TableRow key={page.ID} component="div" classes={{ root: classes.tableRowRoot }}>
      <TableCell style={{ flex: 1 }} classes={{ root: clsx(classes.tableCellRoot, classes.tabelCellPadding) }}>
        <Box p={2}>
          <Typography variant="h6" gutterBottom>
            {page.Name}
          </Typography>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            {page.Domains.join(', ')}
          </Typography>
          <Typography variant="body2">{t('landingPages.popupManagement.tableHeaders.status')}: {page.StatusName}</Typography>
          <Box mt={2}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="caption">{t('landingPages.popupManagement.tableHeaders.allViewers')}</Typography>
                <Typography>
                  {page.AllViews?.toLocaleString() ?? 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption">{t('landingPages.popupManagement.tableHeaders.identifiedViewers')}</Typography>
                <Typography>
                  {page.IdentifiedViewers?.toLocaleString() ?? 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption">{t('landingPages.SubmitsResource1.HeaderText')}</Typography>
                <Typography>
                  {page.Submits?.toLocaleString() ?? '0'}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption">{t('landingPages.popupManagement.tableHeaders.conversions')}</Typography>
                <Typography>
                  {page.Conversions?.toLocaleString() ?? 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption">{t('landingPages.popupManagement.tableHeaders.conversionRate')}</Typography>
                <Typography>
                  {page.ConversionRate
                    ? `${page.ConversionRate}%`
                    : 'N/A'}
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </TableCell>
    </TableRow>
  );

  const renderTableView = () => {
    const rowStyle = { head: classes.tableRowHead, root: classes.tableRowRoot };
    const cellStyle = { head: classes.tableCellHead, body: classes.tableCellBody, root: classes.tableCellRoot };

    return (
      <TableContainer className={classes.tableStyle}>
        <Table className={classes.tableContainer}>
          {!isMobile && (
            <TableHead>
              <TableRow classes={rowStyle} className={classes.alignItemsCenter}>
                <TableCell align='center' classes={cellStyle} className={classes.flex2}>
                  <div>{t('landingPages.popupManagement.tableHeaders.title')}</div>
                  <div>({(t('landingPages.popupManagement.tableHeaders.url'))})</div>
                </TableCell>
                <TableCell align='center' classes={cellStyle} className={classes.flex1}>
                  <div>{t('landingPages.popupManagement.tableHeaders.allViewers')}</div>
                  <div>({t('landingPages.popupManagement.tableHeaders.status')})</div>
                </TableCell>
                <TableCell align='center' classes={cellStyle} className={classes.flex1}>
                  {t('landingPages.popupManagement.tableHeaders.identifiedViewers')}
                </TableCell>
                <TableCell align='center' classes={cellStyle} className={classes.flex1}>
                  {t('landingPages.SubmitsResource1.HeaderText')}
                </TableCell>
                <TableCell align="center" classes={cellStyle} className={classes.flex1}>
                  <div>{t('landingPages.popupManagement.tableHeaders.conversions')}</div>
                  <div>({t('landingPages.popupManagement.tableHeaders.conversionRate')})</div>
                </TableCell>
                <TableCell align='center' classes={cellStyle} className={clsx(classes.flex3, classes.tableActionContainerCell)}>
                  {t('landingPages.popupManagement.tableHeaders.actions')}
                </TableCell>
              </TableRow>
            </TableHead>
          )}
          <TableBody>
            {pages.map(
              isMobile ? renderMobileTableRow : renderDesktopTableRow
            )}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const renderContent = () => {
    if (pagesLoading || isInitialLoad) {
      return <Loader isOpen={true} />;
    }

    if (pages.length === 0) {
      return (
        <Box textAlign="center" py={5}>
          <Typography>{t('common.NoDataTryFilter')}</Typography>
        </Box>
      );
    }

    if (view === 'card') {
      return renderCardView();
    } else {
      return renderTableView();
    }
  };

  const renderToast = () => {
    if (toastMessage) {
      setTimeout(() => {
        setToastMessage(null);
      }, 3000);
      return (
        <Toast data={toastMessage} />
      );
    }
    return null;
  }

  const handleClose = () => {
    setDialogType(null);
  }

  const handleChange = (id: number) => () => {
    const found = restoreArray.includes(id);
    if (found) {
      setRestoreArray(restoreArray.filter(restore => restore !== id));
    } else {
      setRestoreArray([...restoreArray, id]);
    }
  }

  const getRestorDialog = (data: Page[] = []) => {
    if (!data || !Array.isArray(data)) return null;

    return {
      title: t('landingPages.popupManagement.restorePopupTitle'),
      showDivider: false,
      icon: (
        <div className={clsx(classes.dialogIconContent, 'unicode')}>
          {'\uE185'}
        </div>
      ),
      content: (
        <RestorDialogContent
          classes={classes}
          data={data as any}
          currentChecked={restoreArray as any}
          onChange={handleChange}
          dataIdVar='ID'
        />
      ),
      onConfirm: async () => {
        handleClose();
        await dispatch((restoreLandingPages as any)(restoreArray));
        dispatch(getPopupPages(filters));
        setRestoreArray([]);
      }
    }
  }

  const getDeleteDialog = (data?: string | number) => ({
    title: t('Popup.DeletePopupTitle'),
    showDivider: false,
    content: (
      <Typography style={{ fontSize: 18 }}>
        {t('Popup.DeletePopupDesc')}
      </Typography>
    ),
    onConfirm: async () => {
      if (data) {
        handleClose();
        await dispatch(deletePopup(data as number));
        dispatch(getPopupPages(filters));
      }
    }
  });

  const getDuplicateDialog = (data?: string | number) => ({
    title: t('Popup.DuplicatePopupTitle'),
    showDivider: false,
    content: (
      <Typography style={{ fontSize: 18 }}>
        {t('Popup.DuplicatePopupDesc')}
      </Typography>
    ),
    onConfirm: async () => {
      if (data) {
        handleClose();
        setShowLoader(true);
        // @ts-ignore
        await dispatch(duplicteLandingPage(data));
        await dispatch(getPopupPages(filters));
        setShowLoader(false);
        setToastMessage({ type: 'success', message: t('landingPages.duplicationSuccessful') });
      }
    }
  });

  const getEmbedDialog = (data: Page) => {
    const PopupBaseUrl = isProdMode ? "https://l-p.site" : "https://stage.l-p.site";
    const popupId = data.PopupGuid;
    const embedCode = `<script type="text/javascript" src="${PopupBaseUrl}/pulseempopup.js?id=${popupId}"></script>`;

    return {
      title: t('landingPages.popupManagement.actions.embed'),
      showDivider: false,
      content: (
        <Box>
          <Typography style={{ fontSize: 16, marginBottom: 16 }}>
            {t('landingPages.popupManagement.actions.embedMessage')}
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            value={embedCode}
            InputProps={{
              readOnly: true,
            }}
            onClick={(e: any) => e.target.select()}
          />
        </Box>
      ),
      onConfirm: () => {
        navigator.clipboard.writeText(embedCode);
        handleClose();
        setToastMessage({ type: 'success', message: 'Embed code copied to clipboard!' });
      },
      confirmText: t('notifications.copy')
    };
  };

  const renderDialog = () => {
    if (!dialogType) {
      return null;
    }

    const { data, type } = dialogType;

    let currentDialog: any = null;
    switch (type) {
      case 'delete':
        currentDialog = getDeleteDialog(data);
        break;
      case 'restore':
        currentDialog = getRestorDialog(data);
        break;
      case 'duplicate':
        currentDialog = getDuplicateDialog(data);
        break;
      case 'embed':
        currentDialog = getEmbedDialog(data);
        break;
      case 'tier':
        currentDialog = getTierValidationDialog(data as string);
        break;
      default:
        return null;
    }

    if (!currentDialog) return null;
    return (
      <BaseDialog
        classes={classes}
        open={!!dialogType}
        onClose={handleClose}
        onCancel={handleClose}
        {...currentDialog}>
        {currentDialog.content}
      </BaseDialog>
    )
  }

  return (
    <DefaultScreen
      currentPage="landingPages"
      subPage='popupManagement'
      classes={classes}
      containerClass={clsx(classes.management, classes.mb50)}
    >
      {renderTopSection()}

      {renderSearchAndFilterSection()}
      <Box mt={3}>{renderContent()}</Box>
      <TablePagination
        classes={classes}
        rows={totalPages * filters.PageSize}
        rowsPerPage={filters.PageSize}
        onRowsPerPageChange={(val) => {
          setFilters(prev => ({ ...prev, PageSize: val, PageNumber: 1 }));
        }}
        // @ts-ignore
        rowsPerPageOptions={[6, 12, 18]}
        page={filters.PageNumber}
        onPageChange={(p) => setFilters(prev => ({ ...prev, PageNumber: p }))}
      />
      <Loader isOpen={statsLoading || pagesLoading || showLoader} />
      {renderToast()}
      {renderDialog()}
      {showTierPlans && (
        <TierPlans
          classes={classes}
          isOpen={showTierPlans}
          onClose={() => setShowTierPlans(false)}
        />
      )}
      <PopupPreviewModal
        open={previewPopupId !== null}
        onClose={() => setPreviewPopupId(null)}
        popupId={previewPopupId || 0}
        classes={classes}
      />
    </DefaultScreen>
  );
};

export default PopUpManagement;